import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Camera, Mic, ScanFace, ChevronRight, RefreshCw, Shield, ArrowLeft } from 'lucide-react';
import { API_CONFIG } from '@/config/config';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const SKIP_PROCTORING_CHECK = import.meta.env.VITE_SKIP_PROCTORING_CHECK === 'true';

const proctorApi = {
  analyzeFrame: async (imageBlob) => {
    const formData = new FormData();
    formData.append('frame', imageBlob, 'frame.jpg');
    let response;
    try {
      // Route through the backend proxy so health-checking and graceful
      // degradation are handled in one place (proctoring.routes.js).
      response = await fetch(`${API_CONFIG.BASE_URL}/proctor/analyze_frame`, {
        method: 'POST',
        body: formData,
      });
    } catch {
      const err = new Error('PROCTORING_UNREACHABLE');
      err.code = 'PROCTORING_UNREACHABLE';
      throw err;
    }
    if (!response.ok) throw new Error('Failed to analyze frame.');
    return response.json();
  },
};

const CHECK_ITEMS = [
  { key: 'camera', icon: Camera, label: 'Camera access', sub: 'HD video feed active' },
  { key: 'mic', icon: Mic, label: 'Microphone access', sub: 'Audio input detected' },
  { key: 'face', icon: ScanFace, label: 'Face detection', sub: 'Position your face in frame' },
];

function CheckRow({ icon: Icon, label, sub, status, skipped }) {
  return (
    <div className={cn(
      'flex items-center gap-4 rounded-xl border p-4 transition-all duration-500',
      status === 'ok' && 'border-emerald-500/30 bg-emerald-500/5',
      status === 'error' && 'border-red-500/30 bg-red-500/5',
      status === 'loading' && 'border-primary/20 bg-primary/5',
      status === 'pending' && 'border-white/10 bg-white/5',
    )}>
      <div className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-all duration-500',
        status === 'ok' && 'bg-emerald-500/20',
        status === 'error' && 'bg-red-500/20',
        status === 'loading' && 'bg-primary/15',
        status === 'pending' && 'bg-white/10',
      )}>
        {status === 'ok' && <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
        {status === 'error' && <XCircle className="h-5 w-5 text-red-400" />}
        {status === 'loading' && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
        {status === 'pending' && <Icon className="h-5 w-5 text-white/40" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-semibold', status === 'ok' ? 'text-emerald-400' : status === 'error' ? 'text-red-400' : 'text-white')}>{label}</p>
        <p className="text-xs text-white/50 mt-0.5">{skipped ? 'Skipped — proctoring service offline' : sub}</p>
      </div>
      {status === 'ok' && (
        <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
      )}
    </div>
  );
}

function PulsingRing({ active }) {
  if (!active) return null;
  return (
    <>
      <div className="absolute inset-0 rounded-xl border-2 border-primary/60 animate-ping" style={{ animationDuration: '2s' }} />
      <div className="absolute inset-0 rounded-xl border border-primary/30" />
    </>
  );
}

export default function ResourceCheckPage({ onSuccess }) {
  const navigate = useNavigate();
  const [micStatus, setMicStatus] = useState('pending');
  const [cameraStatus, setCameraStatus] = useState('pending');
  const [faceStatus, setFaceStatus] = useState('pending');
  const [faceSkipped, setFaceSkipped] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [micLevel, setMicLevel] = useState(0); // 0–100 (RMS-derived)
  const [micHeard, setMicHeard] = useState(false);
  const [audioInputs, setAudioInputs] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState('default');
  // Track whether the user has clicked "Allow & Check" yet
  const [checkStarted, setCheckStarted] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafMicRef = useRef(null);
  const micHeardRef = useRef(false);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const stopMicMeter = () => {
    if (rafMicRef.current) cancelAnimationFrame(rafMicRef.current);
    rafMicRef.current = null;
    analyserRef.current = null;
    try { audioCtxRef.current?.close?.(); } catch {}
    audioCtxRef.current = null;
  };

  const refreshDeviceLists = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(d => d.kind === 'audioinput');
      setAudioInputs(mics.map(d => ({ deviceId: d.deviceId, label: d.label || 'Microphone' })));
    } catch {
      // non-fatal
    }
  }, []);

  const startMicMeter = useCallback(async (stream) => {
    stopMicMeter();
    setMicLevel(0);
    setMicHeard(false);
    micHeardRef.current = false;

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.75;
      src.connect(analyser);
      analyserRef.current = analyser;

      const buf = new Uint8Array(analyser.fftSize);
      const startedAt = Date.now();
      const listenForMs = 4000;

      const tick = () => {
        const a = analyserRef.current;
        if (!a) return;
        a.getByteTimeDomainData(buf);
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / buf.length); // 0..~1
        const level = Math.max(0, Math.min(100, Math.round(rms * 220))); // scale tuned for speech
        setMicLevel(level);
        if (level >= 10) micHeardRef.current = true; // ~speech gate (also catches loud room noise)

        if (Date.now() - startedAt < listenForMs) {
          rafMicRef.current = requestAnimationFrame(tick);
        } else {
          const ok = micHeardRef.current;
          setMicHeard(ok);
          setMicStatus(ok ? 'ok' : 'error');
          if (!ok) {
            setError(
              'No audio input detected. Check if your microphone is muted, select the correct mic, ' +
              'or disconnect/reconnect earphones, then click \"Retry\".'
            );
          }
        }
      };
      rafMicRef.current = requestAnimationFrame(tick);
    } catch {
      // If WebAudio fails, fall back to permission-level OK (but still allow retry)
      setMicStatus('ok');
    }
  }, []);

  // Browser-native FaceDetector fallback (Chrome/Edge) — used when backend is degraded
  const browserFaceCheck = useCallback(async (videoEl) => {
    if (!('FaceDetector' in window)) return null;   // Firefox / Safari — not supported
    try {
      const detector = new window.FaceDetector({ maxDetectedFaces: 3, fastMode: false });
      const faces    = await detector.detect(videoEl);
      return { detected: faces.length > 0, count: faces.length };
    } catch {
      return null;
    }
  }, []);

  const captureAndAnalyze = useCallback(async () => {
    setFaceStatus('loading');
    setError('');                  // clear any previous face error before each attempt
    if (!videoRef.current || !canvasRef.current) { setFaceStatus('error'); return; }

    const video  = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width  = video.videoWidth  || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      try {
        const result = await proctorApi.analyzeFrame(blob);

        if (SKIP_PROCTORING_CHECK) {
          setFaceSkipped(true); setFaceStatus('ok'); return;
        }

        if (result.service_degraded) {
          // Backend is offline — try browser-native FaceDetector before giving up
          const browser = await browserFaceCheck(video);
          if (browser === null) {
            // Neither service nor browser API available — skip with warning
            setFaceSkipped(true); setFaceStatus('ok'); return;
          }
          if (browser.detected) {
            setFaceStatus('ok');
            setError('');
          } else {
            setFaceStatus('error');
            setError('No face detected. Make sure your face is visible and well-lit.');
          }
          return;
        }

        // Service responded — require exactly one distinct face (deduped on backend)
        const faceCount = result.face_count ?? (result.face_detected ? 1 : 0);
        const multipleFaces = faceCount > 1;
        const singleFace = result.success && result.face_detected === true && !multipleFaces;
        const frontalOk = result.is_frontal !== false; // false when multi-face or not facing camera

        if (singleFace && frontalOk) {
          setFaceStatus('ok');
          setError('');
        } else if (multipleFaces) {
          setFaceStatus('error');
          setError(
            result.message ||
              `${faceCount} faces detected. Only one person is allowed — close mirrors or move others out of frame.`
          );
        } else if (singleFace && !frontalOk) {
          setFaceStatus('error');
          setError(result.message || 'Look directly at the camera with your face centred in the frame.');
        } else {
          setFaceStatus('error');
          setError(result.message || 'No face detected. Make sure your face is visible and well-lit.');
        }

      } catch (err) {
        if (err.code === 'PROCTORING_UNREACHABLE' || SKIP_PROCTORING_CHECK) {
          setFaceSkipped(true); setFaceStatus('ok');
        } else {
          setFaceStatus('error');
          setError('Face detection failed. Make sure your face is clearly visible and well-lit.');
        }
      }
    }, 'image/jpeg', 0.85);
  }, [browserFaceCheck]);

  const startCheck = useCallback(async () => {
    setError('');
    setMicStatus('loading');
    setCameraStatus('loading');
    setFaceStatus('pending');
    setFaceSkipped(false);
    setReady(false);
    setMicLevel(0);
    setMicHeard(false);
    micHeardRef.current = false;
    stopMicMeter();
    stopStream();

    // Guard: getUserMedia requires a secure context (HTTPS or localhost)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(
        'Camera and microphone access requires a secure connection (HTTPS). ' +
        'Please contact your interview organiser or try a different browser.'
      );
      setCameraStatus('error');
      setMicStatus('error');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedMicId && selectedMicId !== 'default'
          ? { deviceId: { exact: selectedMicId } }
          : true,
        video: { width: 1280, height: 720 }
      });
      streamRef.current = stream;
      // After permission grant, we can enumerate devices with labels.
      refreshDeviceLists();
      // Verify real mic input (not just permission).
      startMicMeter(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(() => {});
          setCameraStatus('ok');
          setTimeout(captureAndAnalyze, 1800);
        };
      }
    } catch (err) {
      const isDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      const isNotFound = err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError';

      if (isDenied) {
        setError(
          'Permission denied. Click the camera/lock icon in your browser address bar, ' +
          'allow Camera and Microphone, then click "Retry".'
        );
      } else if (isNotFound) {
        setError('No camera or microphone found. Please connect a device and click "Retry".');
      } else {
        setError('Camera or microphone not accessible. Allow permissions in your browser and retry.');
      }
      setCameraStatus('error');
      setMicStatus('error');
    }
  }, [captureAndAnalyze, refreshDeviceLists, selectedMicId, startMicMeter]);

  // Do NOT auto-start — browsers require a user gesture to call getUserMedia in production.
  // The "Allow & Check" button below provides that gesture.
  // On unmount, only stop the stream if it hasn't been handed off via onSuccess(stream).
  useEffect(() => {
    return () => {
      stopMicMeter();
      stopStream();
    }; // stopStream is a no-op if streamRef.current is null
  }, []);

  useEffect(() => {
    if (micStatus === 'ok' && cameraStatus === 'ok' && faceStatus === 'ok') {
      setTimeout(() => setReady(true), 400);
    }
  }, [micStatus, cameraStatus, faceStatus]);

  const handleGrantPermissions = async () => {
    setCheckStarted(true);
    await startCheck();
  };

  const handleRetry = async () => {
    setCheckStarted(true);
    setRetrying(true);
    await startCheck();
    setRetrying(false);
  };

  const handleRetryFace = async () => {
    setRetrying(true);
    await captureAndAnalyze();
    setRetrying(false);
  };

  const handleContinue = async () => {
    // Do NOT stop the stream here — pass it live to the interview flow so
    // InterviewPage can use it immediately without a 20-30 s re-request delay.
    const stream = streamRef.current;
    streamRef.current = null; // hand off ownership; caller is responsible for stopping
    onSuccess(stream);
  };

  const allOk = micStatus === 'ok' && cameraStatus === 'ok' && faceStatus === 'ok';
  const hasError = micStatus === 'error' || cameraStatus === 'error';

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0a0d14] px-4 py-10">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 h-[400px] w-[400px] rounded-full bg-indigo-500/5 blur-[100px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="relative z-10 w-full max-w-2xl">
        {/* Back to dashboard */}
        <div className="mb-4">
          <button
            onClick={() => { stopStream(); navigate('/dashboard'); }}
            className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/80 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Dashboard
          </button>
        </div>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5">
            <Shield className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold text-primary tracking-wide">SECURE INTERVIEW ENVIRONMENT</span>
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight">System Check</h1>
          <p className="mt-2 text-sm text-white/50">Verify your camera, microphone, and face detection before starting</p>
        </div>

        {/* Main panel */}
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl">
          <div className="grid md:grid-cols-[1fr_1.1fr] divide-y md:divide-y-0 md:divide-x divide-white/10">

            {/* Camera preview */}
            <div className="relative flex flex-col items-center justify-center gap-4 p-6">
              <div className="relative w-full max-w-xs">
                <div className={cn(
                  'relative w-full aspect-[4/3] overflow-hidden rounded-xl border-2 bg-black/80 transition-all duration-500',
                  cameraStatus === 'ok' ? 'border-primary/40' : 'border-white/10'
                )}>
                  <PulsingRing active={faceStatus === 'loading'} />
                  <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover scale-x-[-1]" />
                  <canvas ref={canvasRef} className="hidden" />

                  {/* Corner brackets */}
                  {cameraStatus === 'ok' && (
                    <>
                      <div className="absolute left-2 top-2 h-5 w-5 border-l-2 border-t-2 border-primary rounded-tl" />
                      <div className="absolute right-2 top-2 h-5 w-5 border-r-2 border-t-2 border-primary rounded-tr" />
                      <div className="absolute left-2 bottom-2 h-5 w-5 border-l-2 border-b-2 border-primary rounded-bl" />
                      <div className="absolute right-2 bottom-2 h-5 w-5 border-r-2 border-b-2 border-primary rounded-br" />
                    </>
                  )}

                  {/* Status overlay */}
                  {faceStatus === 'ok' && !faceSkipped && (
                    <div className="absolute bottom-2 inset-x-2 rounded-lg bg-emerald-500/20 border border-emerald-500/30 backdrop-blur-sm px-2 py-1 text-center">
                      <span className="text-[11px] font-semibold text-emerald-400">Face detected</span>
                    </div>
                  )}
                  {faceStatus === 'loading' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                      <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-xs text-white/70">Scanning for face…</p>
                      </div>
                    </div>
                  )}
                  {cameraStatus === 'pending' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <Loader2 className="h-8 w-8 animate-spin text-white/40" />
                    </div>
                  )}
                </div>
              </div>

              {/* Retry face detection (only face failed, camera/mic OK) */}
              {faceStatus === 'error' && cameraStatus === 'ok' && (
                <Button size="sm" variant="outline" onClick={handleRetryFace} disabled={retrying} className="gap-1.5 border-white/20 text-white hover:bg-white/10 text-xs whitespace-nowrap">
                  {retrying
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /><span>Checking…</span></>
                    : <><RefreshCw className="h-3.5 w-3.5" /><span>Retry Face Detection</span></>
                  }
                </Button>
              )}
            </div>

            {/* Status checks */}
            <div className="flex flex-col justify-center gap-3 p-6">
              <p className="text-[11px] font-bold uppercase tracking-widest text-white/40 mb-1">Status</p>

              {CHECK_ITEMS.map(({ key, icon, label, sub }) => {
                const status = !checkStarted ? 'pending'
                  : key === 'camera' ? cameraStatus
                  : key === 'mic'    ? micStatus
                  : faceStatus;
                const skipped = key === 'face' && faceSkipped;
                return <CheckRow key={key} icon={icon} label={label} sub={sub} status={status} skipped={skipped} />;
              })}

              {/* Microphone input meter + selector (shown after user starts the check) */}
              {checkStarted && (
                <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-white/40">Microphone test</p>
                    <span className={cn(
                      'text-[11px] font-semibold',
                      micHeard ? 'text-emerald-400' : micStatus === 'loading' ? 'text-primary' : 'text-amber-300'
                    )}>
                      {micStatus === 'loading' ? 'Listening… say a sentence' : micHeard ? 'Audio detected' : 'No audio detected'}
                    </span>
                  </div>

                  <div className="h-2 w-full overflow-hidden rounded-full bg-black/40 border border-white/10">
                    <div
                      className={cn('h-full transition-all duration-100',
                        micLevel > 20 ? 'bg-emerald-400' : micLevel > 8 ? 'bg-amber-400' : 'bg-red-400')}
                      style={{ width: `${Math.min(100, micLevel)}%` }}
                    />
                  </div>

                  {audioInputs.length > 1 && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-white/50 shrink-0">Input</label>
                      <select
                        value={selectedMicId}
                        onChange={(e) => setSelectedMicId(e.target.value)}
                        className="flex-1 rounded-md bg-black/30 border border-white/10 px-2 py-1 text-xs text-white/80"
                      >
                        <option value="default">Default microphone</option>
                        {audioInputs.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <p className="text-[11px] text-white/40">
                    Tip: If you use earphones/Bluetooth, select the correct input and speak once to confirm levels.
                  </p>
                </div>
              )}

              {error && (
                <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2.5 text-xs text-red-400 leading-relaxed">
                  {error}
                </div>
              )}

              <div className="mt-2 flex flex-col gap-2">
                {/* Pre-check: prompt user to grant permissions via a gesture */}
                {!checkStarted && (
                  <Button
                    size="lg"
                    onClick={handleGrantPermissions}
                    className="w-full gap-2 font-bold text-sm"
                  >
                    <Camera className="h-4 w-4" />
                    Allow Camera &amp; Microphone
                  </Button>
                )}

                {/* Checking in progress */}
                {checkStarted && !allOk && !hasError && (
                  <Button size="lg" disabled className="w-full gap-2 font-bold text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking system…
                  </Button>
                )}

                {/* Camera/mic error — offer full retry */}
                {checkStarted && hasError && (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={handleRetry}
                    disabled={retrying}
                    className="w-full gap-2 font-bold text-sm border-white/20 text-white hover:bg-white/10"
                  >
                    {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Retry
                  </Button>
                )}

                {/* All checks passed */}
                {allOk && (
                  <Button
                    size="lg"
                    onClick={handleContinue}
                    className={cn('w-full gap-2 font-bold text-sm transition-all duration-500', ready && 'animate-in fade-in')}
                  >
                    Start Interview <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <p className="text-center text-[11px] text-white/30">
                {!checkStarted
                  ? 'Click above to grant camera and microphone access'
                  : faceSkipped
                    ? 'Face detection skipped — proctoring may be limited during the interview'
                    : allOk
                      ? 'All checks passed — you\'re ready to begin'
                      : 'All checks must pass before you can start'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
